import { detectBodyBaseY, normalizePolygon, validatePolygon, type Pt, type WallInput } from "@tasduvar/engine";
import { parseTR } from "../../lib/number";

export type SketchAction = { kind: "point"; p: Pt } | { kind: "close" } | { kind: "undo" } | { kind: "cancel" } | { kind: "error"; msg: string };

const r3 = (v: number): number => Math.round(v * 1000) / 1000;

/**
 * CIZ kipinde komut satırı girdisi (AutoCAD LINE sözdizimi, Türkçe ondalık):
 * `x y` / `x;y` mutlak · `@dx dy` göreli · `@L<açı` kutupsal (derece, +x'ten saat yönünün tersine) · K kapat · G geri · IPTAL.
 */
export function parseSketchInput(text: string, points: Pt[]): SketchAction {
  const raw = text.trim();
  const word = raw.toLocaleUpperCase("tr").replace(/İ/g, "I");
  if (!raw) return { kind: "error", msg: "Nokta girin: x y (mutlak) · @dx dy (göreli) · @L<açı · K kapat · G geri · IPTAL" };
  if (["K", "KAPAT", "C", "CLOSE"].includes(word)) return { kind: "close" };
  if (["G", "GERI", "U", "UNDO"].includes(word)) return { kind: "undo" };
  if (["IPTAL", "ESC", "X", "CANCEL"].includes(word)) return { kind: "cancel" };
  const rel = raw.startsWith("@");
  const body = rel ? raw.slice(1) : raw;
  const last = points[points.length - 1];
  if (rel && !last) return { kind: "error", msg: "Göreli koordinat için önce bir nokta girin." };
  const polar = /^([^<]+)<(.+)$/.exec(body);
  if (polar) {
    if (!rel) return { kind: "error", msg: "Kutupsal giriş göreli olmalı: @L<açı" };
    const L = parseTR(polar[1]!), ang = parseTR(polar[2]!);
    if (L === null || ang === null) return { kind: "error", msg: "Kutupsal giriş: @L<açı (ör. @2,5<90)" };
    const a = (ang * Math.PI) / 180;
    return { kind: "point", p: { x: r3(last!.x + L * Math.cos(a)), y: r3(last!.y + L * Math.sin(a)) } };
  }
  const parts = body.split(/[\s;]+/).filter(Boolean);
  if (parts.length !== 2) return { kind: "error", msg: "İki koordinat girin: x y (ondalık virgülle, ör. 1,6 0)" };
  const x = parseTR(parts[0]!), y = parseTR(parts[1]!);
  if (x === null || y === null) return { kind: "error", msg: "Sayı okunamadı: " + raw };
  return { kind: "point", p: rel ? { x: r3(last!.x + x), y: r3(last!.y + y) } : { x: r3(x), y: r3(y) } };
}

/** ORTHO açıkken yeni nokta son noktaya göre baskın eksene kilitlenir. */
export function orthoLock(prev: Pt | undefined, p: Pt): Pt {
  if (!prev) return p;
  return Math.abs(p.x - prev.x) >= Math.abs(p.y - prev.y) ? { x: p.x, y: prev.y } : { x: prev.x, y: p.y };
}

/** Çizimi kapatır: normalize + doğrula; geçerliyse duvarı çokgen kipine alan reçete, değilse hata listesi. */
export function closeSketch(points: Pt[]): { errors: string[] } | { recipe: (w: WallInput) => void; polygon: Pt[]; bodyBaseY: number | undefined } {
  if (points.length < 3) return { errors: ["En az 3 köşe gerekli"] };
  const polygon = normalizePolygon(points);
  const errors = validatePolygon(polygon);
  if (errors.length) return { errors };
  const bodyBaseY = detectBodyBaseY(polygon);
  const yTop = Math.max(...polygon.map((p) => p.y));
  return { polygon, bodyBaseY, recipe: (w) => {
    w.geometry = { mode: "polygon", points: polygon, embedmentDepth: Math.min(w.geometry.embedmentDepth, Math.max(0, yTop - 0.05)), ...(bodyBaseY !== undefined ? { bodyBaseY } : {}) };
  } };
}
